/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import YAML from "yaml";
import * as actions from ".";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Plan, LoadStatus, Edits } from "../../../types";
import { readYamlFile } from "../../../utils";

export interface UseWithPlans {
  plans: Record<string, Plan>;
  planEdits: Record<string, Edits<Plan>>;
  planLoadStatus: LoadStatus;
  loadAllPlans: () => Promise<Plan[]>;
  loadPlanFromYaml: (data: File | string) => Promise<Plan>;
  createPlan: (newData: Partial<Plan>) => Promise<Plan>;
  deletePlan: (plan: Plan) => Promise<void>;
  updatePlan: (newData: Partial<Plan>) => Promise<Plan>;
}

export default function useWithPlans(): UseWithPlans {
  const { plans, planEdits, planLoadStatus } = useAppSelector(
    (state) => state.plans
  );
  const dispatch = useAppDispatch();

  async function loadAllPlans(): Promise<Plan[]> {
    if (planLoadStatus === LoadStatus.IN_PROGRESS) return [];
    return await dispatch(actions.loadPlans()).unwrap();
  }

  async function loadPlanFromYaml(file: File | string): Promise<Plan> {
    let data;
    if (typeof file === "string") data = YAML.parse(file);
    else data = await readYamlFile(file);
    const id = typeof data.id === "string" ? data.id : "";
    const name = typeof data.name === "string" ? data.name : "";
    const desc = typeof data.desc === "string" ? data.desc : "";
    const hidden = typeof data.hidden === "boolean" ? data.hidden : false;
    const deleted = typeof data.deleted === "boolean" ? data.deleted : false;
    const sections = Array.isArray(data.sections) ? data.sections : [];
    return {
      id,
      name,
      desc,
      hidden,
      deleted,
      sections,
    };
  }

  async function createPlan(data: Partial<Plan>): Promise<Plan> {
    if (!data.name) throw new Error("name required");
    return await dispatch(
      actions.addOrUpdatePlan({
        id: data.id || "",
        name: data.name || "",
        desc: data.desc || "",
        sections: data.sections || [],
      })
    ).unwrap();
  }

  async function deletePlan(plan: Plan): Promise<void> {
    await dispatch(actions.deletePlan(plan.id));
  }

  async function updatePlan(data: Partial<Plan>): Promise<Plan> {
    return await dispatch(actions.addOrUpdatePlan(data)).unwrap();
  }

  return {
    plans,
    planEdits,
    planLoadStatus,
    loadAllPlans,
    loadPlanFromYaml,
    createPlan,
    deletePlan,
    updatePlan,
  };
}
